// Which changes have an Approve, Deny or Re-run still running.
//
// The review page's actions are bound to a VISIT to a change, so that a reply
// arriving after the operator moved on cannot be reported as the next visit's
// outcome. But the request itself outlives the visit: an approve merges to
// develop and promotes to main, and it keeps doing so while the operator
// leaves the change and comes back. Each visit used to start with its controls
// unlocked, so the operator could send a second approve, or an opposing deny,
// while the first was still merging -- and the backend holds no lock that would
// stop the two racing.
//
// The lock therefore lives outside any one visit or mounted page, keyed by the
// change. A page asks whether its change has an action running; a visit that
// inherits one re-reads the change when it settles, so the screen shows what
// the change has become rather than the verdict of a visit that is over.

type Listener = () => void

const inflight = new Map<string, Promise<void>>()
const listeners = new Set<Listener>()

function notify() {
  for (const listener of listeners) listener()
}

export const inflightActions = {
  // Marks an action as running for `id`. Returns the function that ends it;
  // ending an action that a later begin() already replaced is a no-op.
  begin(id: string): () => void {
    let settle!: () => void
    const running = new Promise<void>((resolve) => {
      settle = resolve
    })
    inflight.set(id, running)
    notify()
    return () => {
      if (inflight.get(id) !== running) return
      inflight.delete(id)
      settle()
      notify()
    }
  },
  has(id: string): boolean {
    return inflight.has(id)
  },
  // The running action for `id`, as a promise that resolves when it ends.
  running(id: string): Promise<void> | undefined {
    return inflight.get(id)
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  // Test isolation only: forgets every running action.
  reset(): void {
    inflight.clear()
    notify()
  },
}
